import { useState } from "react";
import { Clock, Plus, Trash2 } from "lucide-react";
import { DaySchedule, TimeSlot } from "../../types/Availability";
import { Switch } from "../../@/components/ui/switch";
import { Button } from "../../@/components/ui/button";
import { checkTimeOverlap } from "../../lib/utils";
import TimeSlotPicker from "./TimeSlotPicker";

interface DayScheduleRowProps {
  day: DaySchedule;
  onToggleAvailability: () => void;
  onAddTimeSlot: (day: string, slot: TimeSlot) => void;
  onRemoveTimeSlot: (day: string, index: number) => void;
  onApplyToAll: () => void;
  showApplyToAll: boolean;
  isPreviewOnly: boolean;
}

export default function DayScheduleRow({
  day,
  onToggleAvailability,
  onAddTimeSlot,
  onRemoveTimeSlot,
  onApplyToAll,
  showApplyToAll,
  isPreviewOnly,
}: DayScheduleRowProps) {
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [newSlot, setNewSlot] = useState<TimeSlot | null>(null);
  const [hasOverlap, setHasOverlap] = useState(false);

  const handleTimeSlotAdd = (slot: TimeSlot) => {
    const overlap = checkTimeOverlap(day.timeSlots, slot);
    setHasOverlap(overlap);
    if (!overlap) {
      onAddTimeSlot(day.day, slot);
      setShowTimePicker(false);
      setNewSlot(null);
    }
  };

  const handleCancel = () => {
    setShowTimePicker(false);
    setNewSlot(null);
    setHasOverlap(false);
  };

  const handleOpenTimePicker = () => {
    setShowTimePicker(true);
    setNewSlot(null);
    setHasOverlap(false);
  };

  const handleTimeChange = (start: string, end: string) => {
    const slot = { start, end };
    setHasOverlap(false);
  };

  return (
    <div className="border rounded-lg p-4 space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between space-y-2 sm:space-y-0 flex-wrap gap-2">
        <div className="flex items-center space-x-4">
          <Switch
            checked={day.isAvailable}
            onCheckedChange={onToggleAvailability}
            disabled={isPreviewOnly}
          />
          <span className="font-medium">{day.day}</span>
        </div>
        {day.isAvailable && !isPreviewOnly && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleOpenTimePicker}
            className="w-full sm:w-auto "
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Time Slot
          </Button>
        )}
        {!day.isAvailable && (
          <span className="text-muted-foreground">Not available</span>
        )}
      </div>

      {day.isAvailable && (
        <div className="space-y-2">
          {day.timeSlots
            .sort((a, b) => {
              const dateA = new Date(`1970/01/01 ${a.start}`);
              const dateB = new Date(`1970/01/01 ${b.start}`);
              return dateA.getTime() - dateB.getTime();
            })
            .map((slot, index) => (
              <div
                key={index}
                className="flex items-center justify-between bg-muted p-2 rounded"
              >
                <div className="flex items-center space-x-2">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <span>
                    {slot.start} - {slot.end}
                  </span>
                </div>
                <Button
                  type="button"
                  disabled={isPreviewOnly}
                  variant="ghost"
                  size="sm"
                  onClick={() => onRemoveTimeSlot(day.day, index)}
                >
                  <Trash2 className="w-4 h-4 text-destructive" />
                </Button>
              </div>
            ))}

          {showTimePicker && (
            <TimeSlotPicker
              onSave={handleTimeSlotAdd}
              onCancel={handleCancel}
              hasOverlap={hasOverlap}
              onTimeChange={handleTimeChange}
            />
          )}
          {showApplyToAll && !isPreviewOnly && (
            <span
              onClick={onApplyToAll}
              className="w-full sm:w-auto flex justify-end font-semibold text-customGreenColor cursor-pointer text-sm"
            >
              Apply to All
            </span>
          )}
        </div>
      )}
      {day.isAvailable && day.timeSlots.length === 0 && (
        <div className="text-muted-foreground">
          No time slots available. Please add some time slots.
        </div>
      )}
    </div>
  );
}
